import { useEffect, useState, useCallback } from "react";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  Chip,
  Button,
  Stack,
  CircularProgress,
  TextField,
  MenuItem,
  Link,
  Tooltip,
  Snackbar,
  Alert,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import api from "../../utils/api";

export default function AdminPayoutsPage() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);

  // filters
  const [status, setStatus] = useState("all");
  const [search, setSearch] = useState("");

  // pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);

  // toast
  const [toast, setToast] = useState({
    open: false,
    severity: "info",
    message: "",
  });

  // ===============================
  // LOAD PAYOUTS
  // ===============================
  const loadPayouts = useCallback(async () => {
    try {
      setLoading(true);

      const res = await api.get("/admin/payouts", {
        params: {
          status,
          search,
          page: page + 1,
          limit: rowsPerPage,
        },
      });

      setRows(res.data?.rows || []);
      setTotal(res.data?.total || 0);
    } catch (err) {
      console.error("Failed to load admin payouts", err);
      setRows([]);
      setTotal(0);
      setToast({
        open: true,
        severity: "error",
        message: "Failed to load payouts.",
      });
    } finally {
      setLoading(false);
    }
  }, [status, search, page, rowsPerPage]);

  useEffect(() => {
    loadPayouts();
  }, [loadPayouts]);

  // ===============================
  // ACTION HANDLERS
  // ===============================
  const approve = async (id) => {
    if (!window.confirm("Approve this payout request?")) return;
    await api.post(`/admin/payouts/${id}/approve`);
    setToast({ open: true, severity: "success", message: "Payout approved." });
    loadPayouts();
  };

  const markPaid = async (id) => {
    const ref = prompt("Payment reference (leave empty to auto-generate):");
    await api.post(`/admin/payouts/${id}/mark-paid`, {
      payment_reference: ref || null,
    });
    setToast({ open: true, severity: "success", message: "Payout marked paid." });
    loadPayouts();
  };

  const reject = async (id) => {
    const note = prompt("Reason for rejection:");
    if (!note) return;
    await api.post(`/admin/payouts/${id}/reject`, { note });
    setToast({ open: true, severity: "warning", message: "Payout rejected." });
    loadPayouts();
  };

  // ===============================
  // RENDER
  // ===============================
  return (
    <Box>
      <Typography variant="h5" fontWeight={800} gutterBottom>
        Agent Payouts
      </Typography>

      {/* FILTERS */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems="center"
        >
          <Typography>Status:</Typography>

          <TextField
            select
            size="small"
            value={status}
            sx={{ minWidth: 160 }}
            onChange={(e) => {
              setStatus(e.target.value);
              setPage(0);
            }}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="requested">Requested</MenuItem>
            <MenuItem value="approved">Approved</MenuItem>
            <MenuItem value="paid">Paid</MenuItem>
            <MenuItem value="rejected">Rejected</MenuItem>
          </TextField>

          <TextField
            size="small"
            placeholder="Search agent name or email"
            value={search}
            sx={{ minWidth: 260 }}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
          />
        </Stack>
      </Paper>

      {/* TABLE */}
      <Paper>
        {loading ? (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <CircularProgress />
          </Box>
        ) : rows.length === 0 ? (
          <Box sx={{ p: 4 }}>
            <Typography>No payouts found.</Typography>
          </Box>
        ) : (
          <>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Agent</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Amount (₦)</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Requested</TableCell>
                  <TableCell>Approved</TableCell>
                  <TableCell>Paid</TableCell>
                  <TableCell>Reference</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>
                      <Link
                        component="button"
                        underline="hover"
                        onClick={() =>
                          navigate(`/admin/agents/${row.agent_id}`)
                        }
                      >
                        {row.agent_name}
                      </Link>
                    </TableCell>

                    <TableCell>{row.agent_email}</TableCell>

                    <TableCell>
                      ₦{Number(row.amount_naira).toLocaleString()}
                    </TableCell>

                    <TableCell>
                      <Chip
                        label={row.status}
                        size="small"
                        color={
                          row.status === "paid"
                            ? "success"
                            : row.status === "approved"
                            ? "primary"
                            : row.status === "rejected"
                            ? "error"
                            : "default"
                        }
                      />
                    </TableCell>

                    <TableCell>
                      {row.requested_at
                        ? new Date(row.requested_at).toLocaleDateString()
                        : "—"}
                    </TableCell>

                    <TableCell>
                      {row.approved_at
                        ? new Date(row.approved_at).toLocaleDateString()
                        : "—"}
                    </TableCell>

                    <TableCell>
                      {row.paid_at
                        ? new Date(row.paid_at).toLocaleDateString()
                        : "—"}
                    </TableCell>

                    <TableCell>
                      {row.payment_reference || "—"}
                    </TableCell>

                    <TableCell align="right">
                      <Stack direction="row" spacing={1}>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() =>
                            navigate(`/admin/agents/${row.agent_id}`)
                          }
                        >
                          View Agent
                        </Button>

                        {row.status === "requested" && (
                          <>
                            <Button
                              size="small"
                              variant="contained"
                              onClick={() => approve(row.id)}
                            >
                              Approve
                            </Button>
                            <Button
                              size="small"
                              color="error"
                              onClick={() => reject(row.id)}
                            >
                              Reject
                            </Button>
                          </>
                        )}

                        {row.status === "approved" && (
                          <Tooltip
                            title={
                              row.payout_verified
                                ? "Mark payout as paid"
                                : "Agent bank account not verified"
                            }
                          >
                            <span>
                              <Button
                                size="small"
                                variant="contained"
                                color="success"
                                disabled={!row.payout_verified}
                                onClick={() => markPaid(row.id)}
                              >
                                Mark Paid
                              </Button>
                            </span>
                          </Tooltip>
                        )}

                        {row.status === "paid" && (
                          <Chip label="Locked" size="small" color="success" />
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25]}
            />
          </>
        )}
      </Paper>

      {/* TOAST */}
      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast({ ...toast, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={toast.severity}
          variant="filled"
          onClose={() => setToast({ ...toast, open: false })}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
